import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormHelperText,
  makeStyles,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { LambdaEnvVarsEditor } from '../../../LambdaComponents/phx-api-v2/LambdaEnvVarsEditor';
import { ValkyrieQueueEnvVarsEditor } from './ValkyrieQueueEnvVarsEditor';

const useStyles = makeStyles(theme => ({
  envVarsSection: {
    marginTop: theme.spacing(2),
    '&:before': { display: 'none' },
  },
  envVarsSummary: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    minHeight: 48,
    '&.Mui-expanded': { minHeight: 48 },
  },
  envVarsDetails: {
    flexDirection: 'column',
  },
  envVarsContent: {
    marginTop: theme.spacing(2),
  },
  alwaysAddedVars: {
    fontFamily: 'monospace',
    fontSize: '0.8125rem',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
  },
}));

interface ValkyrieEnvVarsSectionProps {
  envVars: Record<string, string>;
  sqsQueueUrls: Record<string, string>;
  onEnvVarsChange: (v: Record<string, string>) => void;
  onSqsQueueUrlsChange: (v: Record<string, string>) => void;
  queueNames: string[];
}

export function ValkyrieEnvVarsSection({
  envVars,
  sqsQueueUrls,
  onEnvVarsChange,
  onSqsQueueUrlsChange,
  queueNames,
}: ValkyrieEnvVarsSectionProps) {
  const classes = useStyles();
  const staticVars = envVars ?? {};

  return (
    <Accordion className={classes.envVarsSection} defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.envVarsSummary}>
        <Typography variant="subtitle2">Environment Variables</Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.envVarsDetails}>
        <FormHelperText style={{ marginBottom: 4 }}>
          The following variables are always added by Terraform: ASPNETCORE_ENVIRONMENT, DB_HOST,
          DB_NAME, DB_SECRET_NAME, REDIS__ENDPOINT, REDIS__PORT, LEXONE_API_TOKEN_SECRET_NAME,
          LEXONE_API_URL
        </FormHelperText>
        <div className={classes.envVarsContent}>
          <LambdaEnvVarsEditor
            envVars={staticVars}
            title="Static"
            onUpdate={(key, val) => onEnvVarsChange({ ...staticVars, [key]: val })}
            onAdd={() => onEnvVarsChange({ ...staticVars, [`NEW_VAR_${Date.now()}`]: '' })}
            onDelete={key => {
              const next = { ...staticVars };
              delete next[key];
              onEnvVarsChange(next);
            }}
            onRename={(oldKey, newKey) => {
              const next = { ...staticVars };
              const val = next[oldKey];
              delete next[oldKey];
              next[newKey] = val;
              onEnvVarsChange(next);
            }}
          />
          <ValkyrieQueueEnvVarsEditor
            envVars={sqsQueueUrls ?? {}}
            queueNames={queueNames}
            onUpdate={onSqsQueueUrlsChange}
            title="Queue URLs (calculated at apply)"
            description="KEY = env var name | VALUE = queue (URL resolved at apply)"
          />
        </div>
      </AccordionDetails>
    </Accordion>
  );
}
